import React, { useCallback, useMemo } from "react";

import PlayerCardBaseView from "./PlayerCardBaseView";

import { useKanbanColors } from "@/features/player-kanban/hooks/useKanbanColors";

/**
 *
 */
interface PlayerCardBaseContainerProps {
  playerId: string;
  playerName: string;
  rank?: number;
  daysInStage?: number | null;
  isStale?: boolean;
  /** SE-5492: canonical player image URL; null for every player today (SE-5538). */
  imageUrl?: string | null;
  /** SE-5492: explicit position, distinct from the role archetype. */
  position?: string;
  /** SE-5492: total scout reports attached to the player. */
  reportsCount?: number;
  /** SE-5492: latest report's overall score, or null when unscored. */
  verdict?: string | null;
  archetype: string;
  age: number;
  gpr: number;
  contractValue: string;
  contractMonths: number | null;
  agentName: string;
  teamName: string;
  playerAgent?: string;
  /** SE-6490: the backing card's `updatedAt`, driving the footer. */
  updatedAt?: string | null;
  /** SE-6866: the resolved last-editor name completing the footer; null hides it. */
  editorName?: string | null;
  displayedTags: { name: string; color: string }[];
  expandedTagIndex: number | null;
  showAllTags: boolean;
  hasMoreTags: boolean;
  isHovered?: boolean;
  showMoreTags?: boolean;
  isKanban?: boolean;
  onPress: () => void;
  onAddTag?: () => void;
  onRemoveTag?: (tagName: string) => void;
  onToggleTagExpansion: (index: number) => void;
  onToggleShowAllTags: () => void;
  onToggleShowMore?: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  onPreventDrag?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  showRemoveButtons?: boolean;
  bioData?: {
    customStats?: Record<string, unknown> | null;
    minGeminiPlayerValuation?: number | null;
    maxGeminiPlayerValuation?: number | null;
    [key: string]: unknown;
  };
}

/**
 * Container component for base player card managing styling and interaction logic
 * @param root0 - Component props
 * @param root0.playerId - Unique identifier for the player
 * @param root0.playerName - Full name of the player
 * @param root0.rank - Column-scoped 1-based rank badge value (SE-4684)
 * @param root0.daysInStage - SE-5497: whole days in the current stage (pipeline-only)
 * @param root0.isStale - SE-5497: whether the card is over its stage's time target
 * @param root0.imageUrl - SE-5492: canonical player image; null today → initials fallback
 * @param root0.position - SE-5492: explicit position (not the role archetype)
 * @param root0.reportsCount - SE-5492: total scout reports attached to the player
 * @param root0.verdict - SE-5492: latest report's overall score, or null
 * @param root0.archetype - Player archetype or position classification
 * @param root0.age - Player's age in years
 * @param root0.gpr - Gemini Performance Rating score
 * @param root0.contractValue - Formatted contract value string with currency
 * @param root0.contractMonths - Contract duration in months
 * @param root0.agentName - Name of player's agent
 * @param root0.teamName - Name of player's current team
 * @param root0.playerAgent - Additional agent information for display
 * @param root0.updatedAt - SE-6490: the card's last-updated timestamp, driving the footer
 * @param root0.editorName - SE-6866: the resolved last-editor name completing the footer
 * @param root0.displayedTags - Array of tag names currently shown on card
 * @param root0.expandedTagIndex - Index of expanded tag dropdown (-1 if none)
 * @param root0.showAllTags - Whether all tags should be displayed
 * @param root0.hasMoreTags - Whether there are additional tags beyond displayed
 * @param root0.isHovered - Whether card is currently in hover state
 * @param root0.showMoreTags - Whether "show more" tags section is expanded
 * @param root0.isKanban - Whether card is displayed in kanban board context
 * @param root0.onPress - Callback invoked when card is pressed
 * @param root0.onAddTag - Callback invoked to add a new tag to player
 * @param root0.onRemoveTag - Callback invoked when tag is removed with tag name
 * @param root0.onToggleTagExpansion - Callback to toggle tag dropdown with index
 * @param root0.onToggleShowAllTags - Callback to toggle showing all tags
 * @param root0.onToggleShowMore - Callback to toggle "show more" section
 * @param root0.onHoverIn - Callback invoked when mouse enters card area
 * @param root0.onHoverOut - Callback invoked when mouse leaves card area
 * @param root0.onPreventDrag - Callback to prevent drag initiation on certain interactions
 * @param root0.onDragStart - Callback invoked when drag operation begins
 * @param root0.onDragEnd - Callback invoked when drag operation completes
 * @param root0.showRemoveButtons - Whether tag remove buttons should be visible
 * @param root0.bioData - Additional biographical and statistical player data
 */
const PlayerCardBaseContainer = ({
  playerId,
  playerName,
  rank,
  daysInStage,
  isStale,
  imageUrl,
  position,
  reportsCount,
  verdict,
  archetype,
  age,
  gpr,
  contractValue,
  contractMonths,
  agentName,
  teamName,
  playerAgent,
  updatedAt,
  editorName,
  displayedTags,
  expandedTagIndex,
  showAllTags,
  hasMoreTags,
  isHovered = false,
  showMoreTags = false,
  isKanban = false,
  onPress,
  onAddTag,
  onRemoveTag,
  onToggleTagExpansion,
  onToggleShowAllTags,
  onToggleShowMore,
  onHoverIn,
  onHoverOut,
  onPreventDrag,
  onDragStart,
  onDragEnd,
  showRemoveButtons = false,
  bioData,
}: PlayerCardBaseContainerProps) => {
  const colors = useKanbanColors();

  /**
   * SE-6490 — the card's chrome is now TOKENS, not a runtime style object.
   *
   * The old object hard-coded `borderWidth: 2` with `borderColor: colors.primary`
   * (`#2563EB` / `#3B82F6`), so every card carried a permanent 2px blue outline
   * that read as a stuck selection/focus ring — the defect this ticket reports.
   * It is not a focus ring and no state cleared it; it was simply always on.
   *
   * Figma `Player Card v7` binds the card to `background/background0` on
   * `border/border100` — one subtle hairline — which the ratified mapping for
   * this component (`MobileTargetCardView`, per the design registry) expresses
   * as `rounded-md border border-outline-subtle bg-surface-base p-3`. Those
   * classes are reused verbatim in the view, so light/dark resolve from the
   * tokens and no colour is hardcoded.
   *
   * The unconditional 12–16px drop shadow went with it: the design gives the
   * card a border and no shadow, and on a dense board the shadow was noise
   * that made adjacent cards bleed into each other. Hover is expressed as a
   * border-emphasis token in the view instead of a heavier shadow.
   *
   * Nothing is left to compute, so the style object — and the `cardStyle`
   * passthrough that no call site ever supplied — are gone entirely, which
   * also retires one of the card's dynamic-style waivers.
   */

  // Compute custom gestures flag (moved from View)
  const useCustomGestures = useMemo(() => !!onDragStart, [onDragStart]);

  // Memoized pressable style function
  const getPressableStyle = useCallback(
    ({ pressed }: { pressed: boolean }) => [
      {
        transform: [
          { scale: pressed ? 0.95 : isHovered ? 1.02 : 1.0 },
          { translateY: pressed ? 2 : isHovered ? -4 : 0 },
        ],
        transition: "all 0.2s ease",
      },
    ],
    [isHovered]
  );

  return (
    <PlayerCardBaseView
      playerId={playerId}
      playerName={playerName}
      rank={rank}
      daysInStage={daysInStage}
      isStale={isStale}
      imageUrl={imageUrl}
      position={position}
      reportsCount={reportsCount}
      verdict={verdict}
      archetype={archetype}
      age={age}
      gpr={gpr}
      contractValue={contractValue}
      contractMonths={contractMonths}
      agentName={agentName}
      teamName={teamName}
      playerAgent={playerAgent}
      updatedAt={updatedAt}
      editorName={editorName}
      displayedTags={displayedTags}
      _expandedTagIndex={expandedTagIndex}
      _showAllTags={showAllTags}
      _hasMoreTags={hasMoreTags}
      _isHovered={isHovered}
      showMoreTags={showMoreTags}
      isKanban={isKanban}
      getPressableStyle={getPressableStyle}
      useCustomGestures={useCustomGestures}
      colors={colors}
      onPress={onPress}
      onAddTag={onAddTag}
      onRemoveTag={onRemoveTag}
      _onToggleTagExpansion={onToggleTagExpansion}
      _onToggleShowAllTags={onToggleShowAllTags}
      onToggleShowMore={onToggleShowMore}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      onPreventDrag={onPreventDrag}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      showRemoveButtons={showRemoveButtons}
      bioData={bioData}
    />
  );
};

export default PlayerCardBaseContainer;
